<template>
  <div ref="container" :class="containerClass" :style="containerStyle">
    <div v-if="status" class="h-full flex items-center justify-center px-3 text-xs text-slate-400 text-center">
      {{ status }}
    </div>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import * as THREE from 'three'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'
import { useModel3dStore } from '../../stores/model3dStore.js'
import { mapGltfLightsById } from '../../3d/h3dLights.js'
import { applyH3dLightAction } from '../../3d/h3dLightRuntime.js'

const props = defineProps({
  modelId: { type: String, required: true },
  fill: { type: Boolean, default: false },
  borderless: { type: Boolean, default: false },
  controlsElement: { type: Object, default: null },
  backgroundColor: { type: String, default: '#020617' },
  sceneBackgroundColor: { type: String, default: '#020617' },
  sceneBackgroundOpacity: { type: Number, default: 1 },
  preserveDrawingBuffer: { type: Boolean, default: false },
  onCanvasReady: { type: Function, default: null },
  onFrameRendered: { type: Function, default: null },
  onStatusChange: { type: Function, default: null },
  cameraState: { type: Array, default: null },
  onCameraChange: { type: Function, default: null },
  onViewReady: { type: Function, default: null },
  onLightsReady: { type: Function, default: null },
})

const store = useModel3dStore()
const container = ref(null)
const status = ref('')
let renderer = null
let scene = null
let camera = null
let controls = null
let animationFrame = null
let objectUrl = null
let resizeObserver = null
let defaultCameraState = null
let suppressCameraChange = false
let lightsById = new Map()

const containerClass = computed(() => [
  props.fill ? 'h-full w-full' : 'h-64 w-full',
  'min-h-0 overflow-hidden',
  props.borderless ? '' : 'bg-slate-950 rounded border border-slate-700',
])

const containerStyle = computed(() => ({
  backgroundColor: props.backgroundColor,
}))

onMounted(loadPreview)
watch(() => props.modelId, loadPreview)

async function loadPreview() {
  disposeScene()
  if (!props.modelId) return
  setStatus('Loading preview...')
  const model = store.getModel(props.modelId)
  if (!model) {
    setStatus('3D model resource not found.')
    return
  }
  const data = await store.getModelArrayBuffer(props.modelId)
  if (!data || !container.value) {
    setStatus('No model data available.')
    return
  }

  try {
    objectUrl = URL.createObjectURL(new Blob([data], { type: 'model/gltf-binary' }))
    const gltf = await new GLTFLoader().loadAsync(objectUrl)
    if (!container.value) return
    setStatus('')
    lightsById = mapGltfLightsById(gltf)
    setupScene(gltf.scene)
  } catch (e) {
    console.warn('Failed to preview GLB model:', e)
    setStatus('Could not load this GLB preview.')
  }
}

function setStatus(value) {
  status.value = value
  props.onStatusChange?.(value)
}

function setupScene(model) {
  const el = container.value
  const { width, height } = containerSize()
  scene = new THREE.Scene()
  camera = new THREE.PerspectiveCamera(45, width / height, 0.01, 1000)

  scene.add(new THREE.HemisphereLight(0xffffff, 0x334155, 2))
  const keyLight = new THREE.DirectionalLight(0xffffff, 2)
  keyLight.position.set(3, 5, 4)
  scene.add(keyLight)

  scene.add(model)
  frameModel(model)

  renderer = new THREE.WebGLRenderer({
    antialias: true,
    alpha: true,
    preserveDrawingBuffer: props.preserveDrawingBuffer,
  })
  renderer.setClearColor(
    new THREE.Color(props.sceneBackgroundColor || '#020617'),
    Math.max(0, Math.min(Number(props.sceneBackgroundOpacity), 1)),
  )
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
  renderer.setSize(width, height, false)
  renderer.domElement.style.width = '100%'
  renderer.domElement.style.height = '100%'
  renderer.domElement.style.display = 'block'
  el.appendChild(renderer.domElement)
  props.onCanvasReady?.(renderer.domElement)

  controls = new OrbitControls(camera, props.controlsElement || renderer.domElement)
  controls.enableDamping = true
  controls.dampingFactor = 0.08
  defaultCameraState = captureCameraState()
  controls.addEventListener('change', emitCameraState)
  applyCameraState(props.cameraState)
  props.onViewReady?.({ applyView: applyCameraState, resetView })
  props.onLightsReady?.({ lightIds: [...lightsById.keys()], applyLightAction })

  resizeObserver = new ResizeObserver(resize)
  resizeObserver.observe(el)
  animate()
}

function applyLightAction(action) {
  return applyH3dLightAction(lightsById, action)
}

function frameModel(model) {
  const box = new THREE.Box3().setFromObject(model)
  const size = box.getSize(new THREE.Vector3())
  const center = box.getCenter(new THREE.Vector3())
  const maxSize = Math.max(size.x, size.y, size.z) || 1
  const distance = maxSize / (2 * Math.tan((camera.fov * Math.PI) / 360))

  model.position.sub(center)
  camera.position.set(0, maxSize * 0.35, distance * 1.8)
  camera.near = Math.max(distance / 100, 0.01)
  camera.far = distance * 100
  camera.updateProjectionMatrix()
}

function captureCameraState() {
  if (!camera || !controls) return null
  const rounded = value => Number(value.toFixed(6))
  return [
    camera.position.x, camera.position.y, camera.position.z,
    controls.target.x, controls.target.y, controls.target.z,
    camera.fov, camera.zoom,
  ].map(rounded)
}

function applyCameraState(state) {
  if (!camera || !controls || !Array.isArray(state) || state.length < 8) return false
  const values = state.slice(0, 8).map(Number)
  if (!values.every(Number.isFinite)) return false

  suppressCameraChange = true
  const damping = controls.enableDamping
  controls.enableDamping = false
  camera.position.set(values[0], values[1], values[2])
  controls.target.set(values[3], values[4], values[5])
  camera.fov = values[6]
  camera.zoom = values[7]
  camera.updateProjectionMatrix()
  controls.update()
  controls.enableDamping = damping
  suppressCameraChange = false
  return true
}

function resetView() {
  applyCameraState(defaultCameraState)
}

function emitCameraState() {
  if (!suppressCameraChange) props.onCameraChange?.(captureCameraState())
}

function resize() {
  if (!renderer || !camera || !container.value) return
  const { width, height } = containerSize()
  camera.aspect = width / height
  camera.updateProjectionMatrix()
  renderer.setSize(width, height, false)
}

function containerSize() {
  const rect = container.value?.getBoundingClientRect()
  return {
    width: Math.max(1, Math.round(rect?.width ?? 1)),
    height: Math.max(1, Math.round(rect?.height ?? 1)),
  }
}

function animate() {
  animationFrame = requestAnimationFrame(animate)
  controls?.update()
  renderer?.render(scene, camera)
  props.onFrameRendered?.()
}

function disposeScene() {
  if (animationFrame) cancelAnimationFrame(animationFrame)
  resizeObserver?.disconnect()
  controls?.removeEventListener('change', emitCameraState)
  controls?.dispose()
  props.onViewReady?.(null)
  props.onLightsReady?.(null)
  lightsById = new Map()
  if (renderer) props.onCanvasReady?.(null)
  renderer?.dispose()
  renderer?.domElement?.remove()
  if (objectUrl) URL.revokeObjectURL(objectUrl)
  animationFrame = null
  controls = null
  renderer = null
  scene = null
  camera = null
  objectUrl = null
  resizeObserver = null
  defaultCameraState = null
  suppressCameraChange = false
}

onBeforeUnmount(disposeScene)
</script>
